import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Sparkles, Send, Trash2, X } from "lucide-react";
import { API } from "@/lib/api";
import { formatErr } from "@/lib/auth";

const MODELS = [
  { key: "gpt", label: "GPT-5.5" },
  { key: "gemini", label: "Gemini 3.1 Pro" },
];
const SUGGESTIONS = [
  "What are whales doing with BTC today?",
  "Is there sell pressure building on any coin?",
  "Summarise stablecoin flows and what they mean",
];

async function streamChat(body, onDelta) {
  const send = () =>
    fetch(`${API}/ai/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  let res = await send();
  if (res.status === 401) {
    await axios.post(`${API}/auth/refresh`);
    res = await send();
  }
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(j.detail || `Request failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  try {
    for (;;) {
      const { value, done } = await reader.read();
      if (done) break;
      buf += dec.decode(value, { stream: true });
      const events = buf.split("\n\n");
      buf = events.pop();
      events.filter((e) => e.startsWith("data: ")).forEach((e) => {
        const d = JSON.parse(e.slice(6));
        if (d.error) throw new Error(d.error);
        if (d.delta) onDelta(d.delta);
      });
    }
  } catch (e) {
    reader.cancel().catch(() => {});
    throw e;
  }
}

const Bubble = ({ m, i }) => (
  <div data-testid={`ai-message-${i}`} className={`rise max-w-[90%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm leading-relaxed ${
    m.role === "user" ? "ml-auto bg-[#00E5FF] text-[#080B10]" : "border border-[#1E2738] bg-[#161D2A] text-slate-200"}`}>
    {m.content.replace(/\*\*/g, "") || "…"}
    {m.role === "assistant" && m.model && <div className="mt-1 font-mono text-[10px] text-slate-500">{MODELS.find((x) => x.key === m.model)?.label}</div>}
  </div>
);

export default function AnalystChat() {
  const [open, setOpen] = useState(false);
  const [model, setModel] = useState("gpt");
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const end = useRef(null);

  useEffect(() => { if (open) axios.get(`${API}/ai/history`).then((r) => setMsgs(r.data)).catch(() => {}); }, [open]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs]);

  const ask = async (text) => {
    const q = text.trim();
    if (!q || busy) return;
    setInput(""); setErr(""); setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: q }, { role: "assistant", content: "", model }]);
    try {
      await streamChat({ message: q, model }, (d) =>
        setMsgs((m) => [...m.slice(0, -1), { ...m[m.length - 1], content: m[m.length - 1].content + d }]));
    } catch (e) {
      setErr(formatErr(e));
      setMsgs((m) => (m[m.length - 1].content ? m : m.slice(0, -1)));
    }
    setBusy(false);
  };

  const clear = async () => { await axios.delete(`${API}/ai/history`); setMsgs([]); };

  if (!open)
    return (
      <button data-testid="ai-open-button" onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-40 flex items-center gap-2 rounded-full bg-[#00E5FF] px-5 py-3 font-semibold text-[#080B10] shadow-[0_0_30px_rgba(0,229,255,0.35)] transition-transform hover:scale-105">
        <Sparkles className="h-4 w-4" /> Ask AI analyst
      </button>
    );

  return (
    <aside data-testid="ai-chat-panel" className="panel fixed bottom-4 right-4 z-40 flex h-[620px] max-h-[85vh] w-[420px] max-w-[calc(100vw-2rem)] flex-col !bg-[#0F141C]">
      <div className="flex items-center gap-2 border-b border-[#1E2738] p-3">
        <Sparkles className="h-4 w-4 text-[#00E5FF]" />
        <h2 className="font-display font-semibold">AI Whale Analyst</h2>
        <button onClick={clear} data-testid="ai-clear-button" title="Clear chat" className="ml-auto rounded p-1.5 text-slate-500 hover:text-white"><Trash2 className="h-4 w-4" /></button>
        <button onClick={() => setOpen(false)} data-testid="ai-close-button" className="rounded p-1.5 text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>
      </div>
      <div className="flex gap-1 border-b border-[#1E2738] p-2" data-testid="ai-model-select">
        {MODELS.map((m) => (
          <button key={m.key} data-testid={`ai-model-${m.key}`} onClick={() => setModel(m.key)} className={`chip flex-1 ${model === m.key ? "active" : ""}`}>{m.label}</button>
        ))}
      </div>
      <div className="thin-scroll flex-1 space-y-3 overflow-y-auto p-3" data-testid="ai-messages">
        {!msgs.length && (
          <div className="space-y-2">
            <p className="text-sm text-slate-400">Ask about live whale flows. Answers use the last 24h of exchange transfers.</p>
            {SUGGESTIONS.map((s) => (
              <button key={s} onClick={() => ask(s)} data-testid="ai-suggestion" className="block w-full rounded-md border border-[#1E2738] p-2 text-left text-xs text-slate-300 transition-colors hover:border-[#00E5FF]">{s}</button>
            ))}
          </div>
        )}
        {msgs.map((m, i) => <Bubble key={`${m.ts || "live"}-${i}`} m={m} i={i} />)}
        {err && <p className="text-xs text-[#FF3D00]" data-testid="ai-error">{err}</p>}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); ask(input); }} className="flex gap-2 border-t border-[#1E2738] p-3">
        <input data-testid="ai-input" value={input} onChange={(e) => setInput(e.target.value)} disabled={busy} placeholder="Ask about whale moves…"
          className="flex-1 rounded-md border border-[#2E3C54] bg-[#080B10] px-3 text-sm outline-none focus:border-[#00E5FF]" />
        <button data-testid="ai-send-button" disabled={busy || !input.trim()} className="rounded-md bg-[#00E5FF] p-2.5 text-[#080B10] disabled:opacity-40"><Send className="h-4 w-4" /></button>
      </form>
      <p className="px-3 pb-2 font-mono text-[9px] text-slate-600">AI insights are signals, not financial advice.</p>
    </aside>
  );
}
